export type ContentfulPreviewEntry = {
  /**
   * `contentType` is what lets a rich-text embed be rebuilt: it is the only way to know which
   * Gatsby `__typename` an entry maps to (see `entryReference.ts`). The Preview API always sends
   * it; it is optional here because live-preview updates and test fixtures may not.
   */
  sys: { id: string; contentType?: { sys: { id: string } } }
  fields: Record<string, unknown>
}

/**
 * `imageData` is keyed by the option-set keys in `gatsbyHelpers/previewImageData.js`
 * (`IMAGE_DATA_VARIANTS`), each value a ready `gatsbyImageData` object built by the server using
 * Gatsby's own image pipeline. Empty when the asset cannot produce an image — an SVG, or a draft
 * asset Contentful has not processed yet.
 */
export type ContentfulPreviewAsset = {
  sys: { id: string }
  fields: Record<string, unknown>
  imageData?: Record<string, unknown>
}

export type ContentfulPreviewPayload = {
  entries: ContentfulPreviewEntry[]
  assets: ContentfulPreviewAsset[]
}

export const CONTENTFUL_PREVIEW_PROXY_PATH = '/__contentful-preview'

/**
 * Reads a page and everything it links to from the develop-only Preview API proxy.
 *
 * The response is left untransformed: the live preview SDK can only merge edits into
 * shapes it recognises, so reshaping here would silently break live updates. Assets arrive with
 * `imageData` already built, which is what lets an image swap apply synchronously.
 */
export const fetchContentfulPreviewEntries = async (pageContentfulId: string): Promise<ContentfulPreviewPayload> => {
  const response = await fetch(`${CONTENTFUL_PREVIEW_PROXY_PATH}/${pageContentfulId}`)

  if (!response.ok) {
    throw new Error(`Contentful preview request for ${pageContentfulId} failed with ${response.status}`)
  }

  const payload: ContentfulPreviewPayload = await response.json()

  return {
    entries: payload.entries ?? [],
    assets: payload.assets ?? [],
  }
}

/**
 * Reads a single asset the page's include graph did not contain, which is what an image linked
 * after load looks like to us.
 */
export const fetchContentfulPreviewAsset = async (assetId: string): Promise<ContentfulPreviewAsset> => {
  const response = await fetch(`${CONTENTFUL_PREVIEW_PROXY_PATH}/asset/${assetId}`)

  if (!response.ok) {
    throw new Error(`Contentful preview asset request for ${assetId} failed with ${response.status}`)
  }

  return response.json()
}
